import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const MetricCard = ({ icon, title, value, subtitle, color = 'primary', active = false, onClick }) => {
    const colors = {
        primary: '#BD1F34',
        success: '#22C55E',
        warning: '#F59E0B',
        error: '#EF4444',
        info: '#3B82F6'
    };

    return (
        <Card
            elevation={active ? 8 : 2}
            onClick={onClick}
            sx={{
                height: '100%',
                borderTop: `4px solid ${colors[color]}`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                backgroundColor: active ? `${colors[color]}05` : 'background.paper',
                transform: active ? 'translateY(-4px)' : 'none',
                boxShadow: active ? `0 8px 24px ${colors[color]}25` : 'none',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
                    borderColor: colors[color]
                }
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box
                        sx={{
                            backgroundColor: `${colors[color]}15`,
                            borderRadius: '12px',
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {React.cloneElement(icon, {
                            sx: { fontSize: 28, color: colors[color] }
                        })}
                    </Box>
                </Box>

                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2937', mb: 0.5 }}>
                    {value}
                </Typography>

                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                    {title}
                </Typography>

                {subtitle && (
                    <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 1, display: 'block' }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default function InvoiceMetrics({ invoices = [], selectedFilter, onFilterChange }) {
    const { t } = useTranslation("global");
    const totalInvoices = invoices.length;
    const totalAmount = invoices.reduce((sum, inv) => sum + Number(inv.importePendiente * -1), 0);

    const vencidasCount = invoices.filter(inv => inv.estado === 'Pendiente de pago - Vencida').length;
    const sinVencerCount = invoices.filter(inv => inv.estado === 'Pendiente de pago - Sin vencer').length;
    const pendientesCount = invoices.filter(inv => inv.estado === 'Pendiente de pago').length;

    const formatCurrency = (value) => {
        return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <Box sx={{ mb: 4 }}>
            {/* Top Row: General Info & Summary */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <MetricCard
                        icon={<DescriptionIcon />}
                        title={t("facturas.metrics.total_facturas") || "Total Facturas a Pagar"}
                        value={totalInvoices}
                        subtitle={t("facturas.metrics.total_desc") || "Relación completa de facturas"}
                        color="primary"
                        active={!selectedFilter}
                        onClick={() => onFilterChange(null)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MetricCard
                        icon={<AttachMoneyIcon />}
                        title={t("facturas.metrics.importe_total") || "Importe Total Pendiente"}
                        value={`$${formatCurrency(totalAmount)}`}
                        subtitle={t("facturas.metrics.importe_desc") || "Saldo total por pagar"}
                        color="info"
                    />
                </Grid>
            </Grid>

            {/* Bottom Row: Status Filters */}
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t("facturas.metrics.filtrar") || "Filtrar por Estado"}
            </Typography>
            <Grid container spacing={3}>
                {/* Vencidcas - Rojo */}
                <Grid item xs={12} sm={4}>
                    <MetricCard
                        icon={<WarningIcon />}
                        title={t("facturas.metrics.vencidas") || "Vencidas"}
                        value={vencidasCount}
                        subtitle={t("facturas.metrics.del_total", { percent: ((vencidasCount / (totalInvoices || 1)) * 100).toFixed(1) }) || `${((vencidasCount / (totalInvoices || 1)) * 100).toFixed(1)}% del total`}
                        color="error"
                        active={selectedFilter === 'Pendiente de pago - Vencida'}
                        onClick={() => onFilterChange('Pendiente de pago - Vencida')}
                    />
                </Grid>

                {/* Sin Vencer - Verde */}
                <Grid item xs={12} sm={4}>
                    <MetricCard
                        icon={<CheckCircleIcon />}
                        title={t("facturas.metrics.sin_vencer") || "Sin Vencer"}
                        value={sinVencerCount}
                        subtitle={t("facturas.metrics.del_total", { percent: ((sinVencerCount / (totalInvoices || 1)) * 100).toFixed(1) }) || `${((sinVencerCount / (totalInvoices || 1)) * 100).toFixed(1)}% del total`}
                        color="success"
                        active={selectedFilter === 'Pendiente de pago - Sin vencer'}
                        onClick={() => onFilterChange('Pendiente de pago - Sin vencer')}
                    />
                </Grid>

                {/* Pendientes - Naranja */}
                <Grid item xs={12} sm={4}>
                    <MetricCard
                        icon={<WarningIcon />}
                        title={t("facturas.metrics.proximas") || "Próximas a vencer"}
                        value={pendientesCount}
                        subtitle={t("facturas.metrics.del_total", { percent: ((pendientesCount / (totalInvoices || 1)) * 100).toFixed(1) }) || `${((pendientesCount / (totalInvoices || 1)) * 100).toFixed(1)}% del total`}
                        color="warning"
                        active={selectedFilter === 'Pendiente de pago'}
                        onClick={() => onFilterChange('Pendiente de pago')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
